import {
  AspectRatio,
  Box,
  Button,
  Group,
  Modal,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  UnstyledButton,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconArrowLeft,
  IconArrowRight,
  IconBrandWhatsapp,
  IconShoppingBag,
  IconTool,
} from "@tabler/icons-react";
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { DEMO_VIDEO_SELLER, DEMO_VIDEO_SERVICE, WHATSAPP_URL } from "$/data/links";

type Profile = "seller" | "service";

type Ctx = { open: () => void };
const DemoModalContext = createContext<Ctx | null>(null);

export function useDemoModal() {
  const ctx = useContext(DemoModalContext);
  if (!ctx) throw new Error("useDemoModal must be used inside DemoModalProvider");
  return ctx;
}

const profiles: Array<{
  id: Profile;
  title: string;
  description: string;
  examples: string;
  icon: typeof IconShoppingBag;
}> = [
  {
    id: "seller",
    title: "Vendo produtos",
    description: "Vendas, controle de estoque, fiados por cliente e muito mais",
    examples: "Mercadinho · Padaria · Hortifrúti · Açougue · Papelaria",
    icon: IconShoppingBag,
  },
  {
    id: "service",
    title: "Presto serviços",
    description:
      "Catálogo de serviços (e produtos também, se quiser), controle de clientes, agenda e mais.",
    examples: "Barbearia · Salão · Oficina · Manutenção · Autônomo",
    icon: IconTool,
  },
];

const videoUrl: Record<Profile, string> = {
  seller: DEMO_VIDEO_SELLER,
  service: DEMO_VIDEO_SERVICE,
};

export function DemoModalProvider({ children }: { children: ReactNode }) {
  const [opened, { open, close }] = useDisclosure(false);
  const [profile, setProfile] = useState<Profile | null>(null);

  const handleClose = useCallback(() => {
    close();
    setTimeout(() => setProfile(null), 200);
  }, [close]);

  const value = useMemo<Ctx>(() => ({ open }), [open]);

  return (
    <DemoModalContext.Provider value={value}>
      {children}
      <Modal
        opened={opened}
        onClose={handleClose}
        size={profile ? "xl" : 720}
        centered
        radius="xl"
        padding={0}
        withCloseButton={false}
        overlayProps={{ backgroundOpacity: 0.55, blur: 4 }}
        styles={{
          body: { padding: 0 },
        }}
      >
        {profile === null ? (
          <Box pos="relative" style={{ overflow: "hidden" }}>
            <Box
              pos="absolute"
              top={-120}
              right={-100}
              w={320}
              h={320}
              bg="green.1"
              style={{ borderRadius: "50%", filter: "blur(80px)", opacity: 0.6, zIndex: 0 }}
            />
            <Box
              pos="absolute"
              bottom={-140}
              left={-80}
              w={280}
              h={280}
              bg="orange.1"
              style={{ borderRadius: "50%", filter: "blur(90px)", opacity: 0.35, zIndex: 0 }}
            />

            <Stack gap="xl" p={{ base: "xl", sm: 40 }} pos="relative" style={{ zIndex: 1 }}>
              <Stack gap={8}>
                <Group gap={8}>
                  <Box
                    w={8}
                    h={8}
                    bg="green.6"
                    style={{
                      borderRadius: "50%",
                      boxShadow: "0 0 0 4px var(--mantine-color-green-1)",
                    }}
                  />
                  <Text fz="xs" fw={600} c="gray.7" style={{ letterSpacing: "0.04em" }}>
                    DEMONSTRAÇÃO
                  </Text>
                </Group>
                <Text
                  fz={{ base: 26, sm: 32 }}
                  fw={800}
                  c="dark.9"
                  lh={1.15}
                  style={{ letterSpacing: "-0.02em" }}
                >
                  Vamos te mostrar a tela certa.
                </Text>
                <Text c="gray.7" fz={{ base: 14, sm: 15 }} lh={1.55} maw={520}>
                  O Verdana tem fluxos diferentes pra quem vende produtos e pra quem presta serviço.
                  Conta pra gente qual é o seu caso.
                </Text>
              </Stack>

              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                {profiles.map((p) => (
                  <UnstyledButton
                    key={p.id}
                    onClick={() => setProfile(p.id)}
                    p="lg"
                    className="profile-card"
                    style={{
                      border: "1px solid var(--mantine-color-gray-3)",
                      borderRadius: 16,
                      background: "var(--mantine-color-white)",
                      transition: "all 200ms ease",
                      textAlign: "left",
                    }}
                  >
                    <Stack gap="md">
                      <Group justify="space-between" align="flex-start" wrap="nowrap">
                        <ThemeIcon
                          size={52}
                          radius="md"
                          variant="gradient"
                          gradient={{ from: "green.7", to: "green.4", deg: 135 }}
                        >
                          <p.icon size={26} />
                        </ThemeIcon>
                        <ThemeIcon
                          size={28}
                          radius="xl"
                          variant="light"
                          color="gray"
                          className="profile-arrow"
                        >
                          <IconArrowRight size={16} />
                        </ThemeIcon>
                      </Group>
                      <Box>
                        <Text fw={700} fz="lg" c="dark.9" mb={4}>
                          {p.title}
                        </Text>
                        <Text c="gray.7" fz="sm" lh={1.5}>
                          {p.description}
                        </Text>
                      </Box>
                      <Text
                        fz={11}
                        c="gray.6"
                        fw={500}
                        pt="xs"
                        style={{
                          borderTop: "1px dashed var(--mantine-color-gray-3)",
                        }}
                      >
                        {p.examples}
                      </Text>
                    </Stack>
                  </UnstyledButton>
                ))}
              </SimpleGrid>

              <Group
                justify="space-between"
                wrap="wrap"
                gap="sm"
                pt="md"
                style={{ borderTop: "1px solid var(--mantine-color-gray-2)" }}
              >
                <Text fz="xs" c="gray.6">
                  Prefere falar antes de assistir?
                </Text>
                <UnstyledButton
                  component="a"
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener"
                  onClick={handleClose}
                >
                  <Group gap={6}>
                    <IconBrandWhatsapp size={14} color="var(--mantine-color-green-7)" />
                    <Text fz="xs" fw={600} c="green.7">
                      Chama no WhatsApp
                    </Text>
                  </Group>
                </UnstyledButton>
              </Group>
            </Stack>
          </Box>
        ) : (
          <Box>
            <Group
              gap="sm"
              px="lg"
              py="md"
              style={{
                borderBottom: "1px solid var(--mantine-color-gray-2)",
                background: "var(--mantine-color-gray-0)",
              }}
            >
              <UnstyledButton
                onClick={() => setProfile(null)}
                aria-label="Voltar"
                px="xs"
                py={4}
                style={{
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <ThemeIcon variant="subtle" color="gray" size={24}>
                  <IconArrowLeft size={16} />
                </ThemeIcon>
                <Text fz="sm" fw={500} c="gray.7">
                  voltar
                </Text>
              </UnstyledButton>
              <Box style={{ flex: 1 }} />
              <Text fz="sm" fw={600} c="dark.8">
                Demonstração ·{" "}
                <Text component="span" inherit c="green.7">
                  {profile === "seller" ? "venda de produtos" : "prestação de serviços"}
                </Text>
              </Text>
            </Group>

            <Stack gap="lg" p={{ base: "md", sm: "xl" }}>
              <AspectRatio ratio={16 / 9}>
                <Box
                  bg="dark.9"
                  style={{
                    borderRadius: 14,
                    overflow: "hidden",
                    border: "1px solid var(--mantine-color-dark-7)",
                    boxShadow: "0 20px 50px -20px rgba(15, 23, 42, 0.4)",
                  }}
                >
                  <iframe
                    src={videoUrl[profile]}
                    title="Demonstração do Verdana"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    style={{ width: "100%", height: "100%", border: 0 }}
                  />
                </Box>
              </AspectRatio>

              <Group justify="space-between" wrap="wrap" gap="sm">
                <Text c="gray.7" fz="sm" lh={1.5} maw={360}>
                  Gostou? A gente instala pra você, basta chamar no WhatsApp e combinar um horário.
                </Text>
                <Button
                  size="md"
                  variant="gradient"
                  gradient={{ from: "green.7", to: "green.5", deg: 135 }}
                  leftSection={<IconBrandWhatsapp size={18} />}
                  component="a"
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener"
                  onClick={handleClose}
                >
                  Fale com a equipe
                </Button>
              </Group>
            </Stack>
          </Box>
        )}
      </Modal>
    </DemoModalContext.Provider>
  );
}
